import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Progress } from "../components/ui/progress";
import {
  FlaskConical,
  Plus,
  Play,
  Pause,
  CheckCircle,
  Trash2,
  Eye,
  Award,
  Loader2,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Target,
  Zap,
} from "lucide-react";
import { toast } from "sonner";

export default function ABTestingPage() {
  const { api } = useAuth();
  const [tests, setTests] = useState([]);
  const [ssps, setSsps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showResults, setShowResults] = useState(null);
  const [resultsData, setResultsData] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [applyingWinner, setApplyingWinner] = useState(null);
  
  const [newTest, setNewTest] = useState({
    name: "",
    description: "",
    ssp_id: "",
    control_floor: 0.5,
    variant_floor: 0.75,
    traffic_split: 0.5,
    target_sample_size: 1000,
    countries: [],
    ad_formats: [],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [testsRes, sspsRes] = await Promise.all([
        api.get("/ab-tests"),
        api.get("/ssps"),
      ]);
      setTests(testsRes.data);
      setSsps(sspsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const createTest = async () => {
    if (!newTest.name || !newTest.ssp_id) {
      toast.error("Name and SSP are required");
      return;
    }
    
    try {
      const response = await api.post("/ab-tests", newTest);
      setTests([response.data, ...tests]);
      setShowCreate(false);
      setNewTest({
        name: "",
        description: "",
        ssp_id: "",
        control_floor: 0.5,
        variant_floor: 0.75,
        traffic_split: 0.5,
        target_sample_size: 1000,
        countries: [],
        ad_formats: [],
      });
      toast.success("A/B test created successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create test");
    }
  };

  const toggleTestStatus = async (test) => {
    const newStatus = test.status === "active" ? "paused" : "active";
    try {
      await api.put(`/ab-tests/${test.id}`, { status: newStatus });
      setTests(tests.map(t => 
        t.id === test.id ? { ...t, status: newStatus } : t
      ));
      toast.success(`Test ${newStatus === "active" ? "resumed" : "paused"}`);
    } catch (error) {
      toast.error("Failed to update test status");
    }
  };

  const deleteTest = async () => {
    try {
      await api.delete(`/ab-tests/${deleteId}`);
      setTests(tests.filter(t => t.id !== deleteId));
      toast.success("Test deleted");
    } catch (error) {
      toast.error("Failed to delete test");
    } finally {
      setDeleteId(null);
    }
  };

  const viewResults = async (testId) => {
    setShowResults(testId);
    setLoadingResults(true);
    try {
      const response = await api.get(`/ab-tests/${testId}/results`);
      setResultsData(response.data);
    } catch (error) {
      toast.error("Failed to load results");
    } finally {
      setLoadingResults(false);
    }
  };

  const applyWinner = async (testId) => {
    setApplyingWinner(testId);
    try {
      const response = await api.post(`/ab-tests/${testId}/apply-winner`);
      toast.success(`Winner applied! Floor set to $${response.data.applied_floor}`);
      fetchData();
      setShowResults(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply winner");
    } finally {
      setApplyingWinner(null);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
      paused: "bg-amber-500/10 text-amber-500 border-amber-500/20",
      completed: "bg-blue-500/10 text-blue-500 border-blue-500/20",
    };
    const icons = {
      active: Play,
      paused: Pause,
      completed: CheckCircle,
    };
    const Icon = icons[status] || Play;
    
    return (
      <Badge className={`${variants[status] || variants.active} gap-1`} variant="outline">
        <Icon className="h-3 w-3" />
        {status}
      </Badge>
    );
  };

  const activeCount = tests.filter(t => t.status === "active").length;
  const completedCount = tests.filter(t => t.status === "completed").length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ab-testing-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <FlaskConical className="h-8 w-8 text-accent" />
            A/B Testing
          </h1>
          <p className="text-muted-foreground">
            Scientifically test floor price changes to maximize revenue
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-test-btn">
          <Plus className="mr-2 h-4 w-4" />
          New A/B Test
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <FlaskConical className="h-4 w-4 text-accent" />
            <span className="text-sm">Total Tests</span>
          </div>
          <p className="text-2xl font-bold font-mono">{tests.length}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <Play className="h-4 w-4 text-emerald-500" />
            <span className="text-sm">Active</span>
          </div>
          <p className="text-2xl font-bold font-mono text-emerald-500">{activeCount}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <CheckCircle className="h-4 w-4 text-blue-500" />
            <span className="text-sm">Completed</span>
          </div>
          <p className="text-2xl font-bold font-mono text-blue-500">{completedCount}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <Pause className="h-4 w-4 text-amber-500" />
            <span className="text-sm">Paused</span>
          </div>
          <p className="text-2xl font-bold font-mono text-amber-500">
            {tests.filter(t => t.status === "paused").length}
          </p>
        </Card>
      </div>

      {/* Tests Table */}
      <Card>
        <CardHeader>
          <CardTitle>A/B Tests</CardTitle>
          <CardDescription>View and manage floor price experiments</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
          ) : tests.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FlaskConical className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">No A/B tests yet</p>
              <p className="text-sm">Create your first test to start optimizing floor prices</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Test Name</TableHead>
                  <TableHead>SSP</TableHead>
                  <TableHead>Control Floor</TableHead>
                  <TableHead>Variant Floor</TableHead>
                  <TableHead>Traffic Split</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tests.map((test) => (
                  <TableRow key={test.id} data-testid={`test-row-${test.id}`}>
                    <TableCell className="font-medium">
                      <div>
                        <p>{test.name}</p>
                        {test.description && (
                          <p className="text-xs text-muted-foreground">{test.description}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{test.ssp_name}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      ${test.control_floor?.toFixed(4)}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      ${test.variant_floor?.toFixed(4)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={test.traffic_split * 100} className="w-16 h-2" />
                        <span className="text-xs text-muted-foreground">
                          {Math.round(test.traffic_split * 100)}%
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(test.status)}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(test.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewResults(test.id)}
                          data-testid={`view-results-${test.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {test.status !== "completed" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleTestStatus(test)}
                            data-testid={`toggle-status-${test.id}`}
                          >
                            {test.status === "active" ? (
                              <Pause className="h-4 w-4" />
                            ) : (
                              <Play className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDeleteId(test.id)}
                          data-testid={`delete-test-${test.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Test Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create A/B Test</DialogTitle>
            <DialogDescription>
              Set up a new experiment to test different floor prices
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Test Name *</Label>
              <Input
                placeholder="e.g., Banner Floor Optimization Q1"
                value={newTest.name}
                onChange={(e) => setNewTest({ ...newTest, name: e.target.value })}
                data-testid="test-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="Optional description"
                value={newTest.description}
                onChange={(e) => setNewTest({ ...newTest, description: e.target.value })}
                data-testid="test-description-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>SSP Endpoint *</Label>
              <Select
                value={newTest.ssp_id}
                onValueChange={(value) => setNewTest({ ...newTest, ssp_id: value })}
              >
                <SelectTrigger data-testid="ssp-select">
                  <SelectValue placeholder="Select SSP" />
                </SelectTrigger>
                <SelectContent>
                  {ssps.map((ssp) => (
                    <SelectItem key={ssp.id} value={ssp.id}>
                      {ssp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Control Floor ($)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={newTest.control_floor}
                  onChange={(e) => {
                    const val = e.target.value;
                    // Allow empty, digits, and one decimal point
                    if (val === "" || /^\d*\.?\d*$/.test(val)) {
                      setNewTest({ ...newTest, control_floor: val });
                    }
                  }}
                  onBlur={(e) => {
                    const parsed = parseFloat(e.target.value);
                    setNewTest({ ...newTest, control_floor: isNaN(parsed) ? 0 : parsed });
                  }}
                  data-testid="control-floor-input"
                />
                <p className="text-xs text-muted-foreground">Current baseline</p>
              </div>
              <div className="space-y-2">
                <Label>Variant Floor ($)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={newTest.variant_floor}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || /^\d*\.?\d*$/.test(val)) {
                      setNewTest({ ...newTest, variant_floor: val });
                    }
                  }}
                  onBlur={(e) => {
                    const parsed = parseFloat(e.target.value);
                    setNewTest({ ...newTest, variant_floor: isNaN(parsed) ? 0 : parsed });
                  }}
                  data-testid="variant-floor-input"
                />
                <p className="text-xs text-muted-foreground">New floor to test</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between">
                <Label>Traffic Split</Label>
                <span className="text-sm text-muted-foreground">
                  {Math.round(newTest.traffic_split * 100)}% Variant / {Math.round((1 - newTest.traffic_split) * 100)}% Control
                </span>
              </div>
              <Slider
                value={[newTest.traffic_split * 100]}
                onValueChange={([value]) => setNewTest({ ...newTest, traffic_split: value / 100 })}
                max={100}
                step={5}
                data-testid="traffic-split-slider"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Target Sample Size</Label>
              <Input
                type="text"
                inputMode="numeric"
                placeholder="1000"
                value={newTest.target_sample_size}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "" || /^\d*$/.test(val)) {
                    setNewTest({ ...newTest, target_sample_size: val });
                  }
                }}
                onBlur={(e) => {
                  const parsed = parseInt(e.target.value);
                  setNewTest({ ...newTest, target_sample_size: isNaN(parsed) || parsed < 100 ? 1000 : parsed });
                }}
                data-testid="sample-size-input"
              />
              <p className="text-xs text-muted-foreground">
                Number of impressions needed for statistical significance
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button onClick={createTest} data-testid="create-test-submit">
              Create Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Results Dialog */}
      <Dialog open={!!showResults} onOpenChange={() => setShowResults(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-accent" />
              Test Results
            </DialogTitle>
            <DialogDescription>
              Statistical analysis of floor price experiment
            </DialogDescription>
          </DialogHeader>
          
          {loadingResults ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
          ) : resultsData && (
            <div className="space-y-6 py-4">
              {/* Progress */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Test Progress</span>
                  <span className="font-mono">
                    {resultsData.total_sample_size} / {resultsData.target_sample_size}
                  </span>
                </div>
                <Progress value={resultsData.progress_percentage} className="h-3" />
                <p className="text-xs text-muted-foreground text-right">
                  {resultsData.progress_percentage}% complete
                </p>
              </div>
              
              {/* Metrics Comparison */}
              <div className="grid grid-cols-2 gap-4">
                {/* Control */}
                <Card className="p-4 border-2 border-muted">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold">Control</h4>
                    <Badge variant="outline">Floor: ${resultsData.control_metrics.floor?.toFixed(4)}</Badge>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Impressions</span>
                      <span className="font-mono">{resultsData.control_metrics.impressions.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Wins</span>
                      <span className="font-mono">{resultsData.control_metrics.wins.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Win Rate</span>
                      <span className="font-mono">{resultsData.control_metrics.win_rate.toFixed(2)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Revenue</span>
                      <span className="font-mono text-emerald-500">${resultsData.control_metrics.revenue.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Avg Bid</span>
                      <span className="font-mono">${resultsData.control_metrics.avg_bid_price.toFixed(4)}</span>
                    </div>
                  </div>
                </Card>
                
                {/* Variant */}
                <Card className={`p-4 border-2 ${resultsData.winner === "variant" ? "border-emerald-500" : "border-muted"}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold flex items-center gap-2">
                      Variant
                      {resultsData.winner === "variant" && (
                        <Award className="h-4 w-4 text-emerald-500" />
                      )}
                    </h4>
                    <Badge variant="outline">Floor: ${resultsData.variant_metrics.floor?.toFixed(4)}</Badge>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Impressions</span>
                      <span className="font-mono">{resultsData.variant_metrics.impressions.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Wins</span>
                      <span className="font-mono">{resultsData.variant_metrics.wins.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Win Rate</span>
                      <span className="font-mono flex items-center gap-1">
                        {resultsData.variant_metrics.win_rate.toFixed(2)}%
                        {resultsData.variant_metrics.win_rate > resultsData.control_metrics.win_rate ? (
                          <TrendingUp className="h-3 w-3 text-emerald-500" />
                        ) : resultsData.variant_metrics.win_rate < resultsData.control_metrics.win_rate ? (
                          <TrendingDown className="h-3 w-3 text-red-500" />
                        ) : null}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Revenue</span>
                      <span className="font-mono text-emerald-500 flex items-center gap-1">
                        ${resultsData.variant_metrics.revenue.toFixed(2)}
                        {resultsData.variant_metrics.revenue > resultsData.control_metrics.revenue ? (
                          <TrendingUp className="h-3 w-3 text-emerald-500" />
                        ) : resultsData.variant_metrics.revenue < resultsData.control_metrics.revenue ? (
                          <TrendingDown className="h-3 w-3 text-red-500" />
                        ) : null}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Avg Bid</span>
                      <span className="font-mono">${resultsData.variant_metrics.avg_bid_price.toFixed(4)}</span>
                    </div>
                  </div>
                </Card>
              </div>
              
              {/* Statistical Analysis */}
              <Card className="p-4 bg-muted/30">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-accent/10 rounded-lg">
                    <Target className="h-6 w-6 text-accent" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold">Statistical Significance</span>
                      <Badge 
                        variant="outline"
                        className={
                          resultsData.confidence_level === "High" 
                            ? "bg-emerald-500/10 text-emerald-500" 
                            : resultsData.confidence_level === "Medium"
                            ? "bg-amber-500/10 text-amber-500"
                            : "bg-muted text-muted-foreground"
                        }
                      >
                        {resultsData.confidence_level} Confidence
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 mb-3">
                      <Progress value={resultsData.statistical_significance} className="flex-1 h-2" />
                      <span className="font-mono text-sm">{resultsData.statistical_significance}%</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{resultsData.recommendation}</p>
                  </div>
                </div>
              </Card>
              
              {/* Winner Badge */}
              {resultsData.winner && resultsData.winner !== "tie" && (
                <div className="flex items-center justify-center gap-2 p-4 bg-emerald-500/10 rounded-lg">
                  <Award className="h-5 w-5 text-emerald-500" />
                  <span className="font-semibold text-emerald-500">
                    {resultsData.winner === "variant" ? "Variant" : "Control"} is the winner!
                  </span>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResults(null)}>
              Close
            </Button>
            {resultsData && resultsData.winner && resultsData.winner !== "tie" && resultsData.status !== "completed" && (
              <Button 
                onClick={() => applyWinner(showResults)} 
                disabled={applyingWinner === showResults}
                data-testid="apply-winner-btn"
              >
                {applyingWinner === showResults ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Zap className="mr-2 h-4 w-4" />
                )}
                Apply Winner to SSP
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete A/B Test</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this test? All associated data will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteTest} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
